import type { Codespace } from '../types';

export type GitHubCodespace = {
  name?: string;
  display_name?: string | null;
  repository?: {
    full_name?: string;
  };
  git_status?: {
    ref?: string;
  };
  last_used_at?: string | null;
  retention_expires_at?: string | null;
  state?: string | null;
  web_url?: string | null;
};

function parseRepositoryParts(fullRepositoryName?: string): { owner: string; repoName: string } {
  if (!fullRepositoryName) {
    return { owner: 'unknown', repoName: 'unknown' };
  }

  const [owner, repoName] = fullRepositoryName.split('/');
  if (!owner || !repoName) {
    return { owner: 'unknown', repoName: fullRepositoryName };
  }

  return { owner, repoName };
}

function formatRelativeTime(isoString?: string): string {
  if (!isoString) {
    return 'Unknown';
  }

  const timestamp = new Date(isoString).getTime();
  if (Number.isNaN(timestamp)) {
    return 'Unknown';
  }

  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) {
    return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 7) {
    return `${days} day${days !== 1 ? 's' : ''} ago`;
  }

  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks !== 1 ? 's' : ''} ago`;
}

function getAutoDeleteWarningLabel(autoDeleteAt?: string): string | undefined {
  if (!autoDeleteAt) {
    return undefined;
  }

  const deleteAtTimestamp = new Date(autoDeleteAt).getTime();
  if (Number.isNaN(deleteAtTimestamp)) {
    return undefined;
  }

  const msUntilDelete = deleteAtTimestamp - Date.now();
  const oneWeekMs = 1000 * 60 * 60 * 24 * 7;
  if (msUntilDelete <= 0 || msUntilDelete > oneWeekMs) {
    return undefined;
  }

  const daysLeft = Math.ceil(msUntilDelete / (1000 * 60 * 60 * 24));
  return `Auto-delete in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
}

function normalizeStateLabel(rawState?: string): string {
  if (!rawState) {
    return 'Unknown';
  }

  const stateMap: Record<string, string> = {
    Available: 'Running',
    Shutdown: 'Stopped',
    Provisioning: 'Creating',
  };

  return stateMap[rawState] || rawState.charAt(0).toUpperCase() + rawState.slice(1);
}

export function mapToCodespace(cs: GitHubCodespace): Codespace {
  const fullRepositoryName = cs.repository?.full_name;
  const { owner, repoName } = parseRepositoryParts(fullRepositoryName);
  const stateLabel = normalizeStateLabel(cs.state ?? undefined);

  return {
    id: cs.name!,
    name: cs.display_name ?? cs.name!,
    owner,
    repoName,
    branch: cs.git_status?.ref ?? '-',
    stateLabel,
    isRunning: stateLabel === 'Running',
    lastUsedRelative: formatRelativeTime(cs.last_used_at ?? undefined),
    autoDeleteWarningLabel: getAutoDeleteWarningLabel(cs.retention_expires_at ?? undefined),
    webUrl: cs.web_url!,
  };
}
